import { useMemo, useState } from "react";
import { Banknote } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { IMG, RACHAT_CATALOG } from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

export default function Buyback() {
  const brands = Object.keys(RACHAT_CATALOG.brands);
  const [brandIdx, setBrandIdx] = useState(0);
  const [modelIdx, setModelIdx] = useState(0);
  const [stateIdx, setStateIdx] = useState(1);

  const brand = brands[brandIdx];
  const models = RACHAT_CATALOG.brands[brand];
  const [model, basePrice] = models[modelIdx] || models[0];
  const [stateLabel, mult] = RACHAT_CATALOG.states[stateIdx] || RACHAT_CATALOG.states[1];

  const range = useMemo(() => {
    const mid = Number(basePrice) * Number(mult);
    const lo = Math.max(10, Math.round((mid * 0.9) / 5) * 5);
    const hi = Math.max(20, Math.round((mid * 1.1) / 5) * 5);
    return [lo, hi];
  }, [basePrice, mult]);

  const onBrandChange = (e) => {
    setBrandIdx(Number(e.target.value));
    setModelIdx(0);
  };

  const send = () => {
    window.dispatchEvent(
      new CustomEvent("prefill-devis", {
        detail: {
          message: `RACHAT — ${brand} ${model} · État : ${stateLabel} · Estimation indicative : ${range[0]} € à ${range[1]} €`,
        },
      })
    );
    scrollToSection("#devis");
  };

  return (
    <section id="rachat" className="relative overflow-hidden border-y border-line bg-ink py-24 lg:py-32">
      <img
        src={IMG.motherboard}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-1/2 hidden h-[520px] w-[520px] -translate-y-1/2 rounded-full object-cover opacity-15 lg:block"
        loading="lazy"
      />
      <div className="relative mx-auto w-full max-w-7xl px-5 lg:px-8">
        <SectionHead
          tag="Rachat téléphone"
          title={<>Votre ancien téléphone <span className="text-neon">vaut de l'argent</span></>}
          sub="Vendez-nous votre smartphone en toute simplicité : estimation indicative immédiate calée sur les prix de reprise du marché (type Back Market), offre ferme après un contrôle en atelier de 2 minutes."
        />

        <Reveal delay={0.1}>
          <div className="mt-14 grid gap-6 rounded-2xl border border-line bg-night p-7 lg:grid-cols-[1fr_auto] lg:p-9">
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="flex flex-col gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Marque</span>
                <select data-testid="buyback-brand-select" className="field-input" value={brandIdx} onChange={onBrandChange}>
                  {brands.map((b, i) => <option key={b} value={i}>{b}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Modèle</span>
                <select data-testid="buyback-model-select" className="field-input" value={modelIdx} onChange={(e) => setModelIdx(Number(e.target.value))}>
                  {models.map(([m], i) => <option key={m} value={i}>{m}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">État</span>
                <select data-testid="buyback-state-select" className="field-input" value={stateIdx} onChange={(e) => setStateIdx(Number(e.target.value))}>
                  {RACHAT_CATALOG.states.map(([s], i) => <option key={s} value={i}>{s}</option>)}
                </select>
              </label>
            </div>

            <div className="flex flex-col items-start justify-center gap-4 rounded-xl border border-neon/30 bg-neon/5 p-6 lg:w-72">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Estimation indicative</p>
              <p data-testid="buyback-estimate" className="font-display text-4xl font-extrabold leading-none text-white">
                {range[0]} € <span className="text-slate-500">–</span> {range[1]} €
              </p>
              <button
                data-testid="buyback-submit-btn"
                onClick={send}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-neon px-6 py-3 text-xs font-extrabold uppercase tracking-wider text-night chip-glow transition-transform duration-200 hover:scale-[1.03]"
              >
                <Banknote size={16} />
                Recevoir mon offre ferme
              </button>
              <p className="text-[11px] leading-relaxed text-slate-500">
                {RACHAT_CATALOG.note}
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
            <div className="rounded-3xl border border-line bg-ink p-7 lg:p-9">
              <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-white">
                Comment se passe le rachat&nbsp;?
              </h3>
              <ol className="mt-5 grid gap-3 text-sm leading-relaxed text-slate-400">
                <li><strong className="font-semibold text-white">1. Estimez en ligne</strong> — choisissez la marque, le modèle et l'état : l'estimation est immédiate et indicative.</li>
                <li><strong className="font-semibold text-white">2. Passez à l'atelier</strong> — 74800 Saint-Pierre-en-Faucigny, du lundi au vendredi de 09h00 à 19h00, avec votre appareil.</li>
                <li><strong className="font-semibold text-white">3. Contrôle en 2 minutes</strong> — état, fonctionnalités, verrouillages : nous vous faisons une offre ferme.</li>
                <li><strong className="font-semibold text-white">4. Paiement immédiat</strong> — vous acceptez, vous êtes payé sur place ; vous refusez, vous repartez avec votre téléphone.</li>
              </ol>
            </div>
            <div className="rounded-3xl border border-line bg-ink p-7 lg:p-9">
              <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-white">
                Prix de rachat indicatifs par modèle
              </h3>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Modèle · comme neuf · bon état · écran cassé · ne s'allume plus
              </p>
              <div className="mt-5 grid gap-6">
                <div>
                  <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Apple</h4>
                  <ul className="mt-2 grid gap-1 text-[13px] leading-relaxed text-slate-400">
                    {RACHAT_CATALOG.brands.Apple.map(([m, p]) => {
                      const r = (x) => Math.max(10, Math.round(x / 5) * 5);
                      return <li key={m}>{m} : {r(Number(p))} € / {r(Number(p) * 0.85)} € / {r(Number(p) * 0.5)} € / {r(Number(p) * 0.3)} €</li>;
                    })}
                  </ul>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Samsung</h4>
                    <ul className="mt-2 grid gap-1 text-[13px] leading-relaxed text-slate-400">
                      {RACHAT_CATALOG.brands.Samsung.map(([m, p]) => {
                        const r = (x) => Math.max(10, Math.round(x / 5) * 5);
                        return <li key={m}>{m} : {r(Number(p))} € / {r(Number(p) * 0.85)} € / {r(Number(p) * 0.5)} € / {r(Number(p) * 0.3)} €</li>;
                      })}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Google &amp; Xiaomi</h4>
                    <ul className="mt-2 grid gap-1 text-[13px] leading-relaxed text-slate-400">
                      {[...RACHAT_CATALOG.brands.Google, ...RACHAT_CATALOG.brands.Xiaomi].map(([m, p]) => {
                        const r = (x) => Math.max(10, Math.round(x / 5) * 5);
                        return <li key={m}>{m} : {r(Number(p))} € / {r(Number(p) * 0.85)} € / {r(Number(p) * 0.5)} € / {r(Number(p) * 0.3)} €</li>;
                      })}
                    </ul>
                  </div>
                </div>
              </div>
              <p className="mt-5 text-[11px] leading-relaxed text-slate-500">
                Estimations indicatives basées sur les prix de reprise du marché : elles varient selon l'état exact, la capacité de stockage et les
                accessoires fournis. Un modèle qui ne figure pas dans la liste ? Demandez via le devis. Offre ferme après contrôle en atelier
                (2 minutes), paiement immédiat.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
